import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", company: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await register(form);
    setBusy(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-5 py-12">
      <div className="absolute inset-0 bg-grid opacity-40" />
      <div className="relative w-full max-w-md rounded-3xl border border-[#1C2A38]/10 bg-[var(--surface)] p-8 shadow-2xl" data-testid="register-card">
        <div className="mb-8 flex flex-col items-center text-center">
          <Logo />
          <h1 className="mt-6 font-display text-2xl font-800 text-[#1C2A38]">Create your account</h1>
          <p className="mt-1.5 text-sm text-[#5b6878]">Start sourcing smarter — it's free</p>
        </div>

        {error && (
          <div className="mb-5 flex items-center gap-2 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-600" data-testid="register-error">
            <AlertCircle className="h-4 w-4 shrink-0" /> {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-[#3c4a5a]">Full name</Label>
            <Input required value={form.name} onChange={set("name")} data-testid="register-name" placeholder="Ada Lovelace"
              className="mt-1.5 border-[#1C2A38]/15 bg-[var(--surface-2)] text-[#1C2A38] placeholder:text-[#8593a3] focus-visible:ring-[var(--teal)]" />
          </div>
          <div>
            <Label className="text-[#3c4a5a]">Email</Label>
            <Input type="email" required value={form.email} onChange={set("email")} data-testid="register-email" placeholder="you@company.com"
              className="mt-1.5 border-[#1C2A38]/15 bg-[var(--surface-2)] text-[#1C2A38] placeholder:text-[#8593a3] focus-visible:ring-[var(--teal)]" />
          </div>
          <div>
            <Label className="text-[#3c4a5a]">Company <span className="text-[#8593a3]">(optional)</span></Label>
            <Input value={form.company} onChange={set("company")} data-testid="register-company" placeholder="Acme Robotics"
              className="mt-1.5 border-[#1C2A38]/15 bg-[var(--surface-2)] text-[#1C2A38] placeholder:text-[#8593a3] focus-visible:ring-[var(--teal)]" />
          </div>
          <div>
            <Label className="text-[#3c4a5a]">Password</Label>
            <Input type="password" required value={form.password} onChange={set("password")} data-testid="register-password" placeholder="At least 6 characters"
              className="mt-1.5 border-[#1C2A38]/15 bg-[var(--surface-2)] text-[#1C2A38] placeholder:text-[#8593a3] focus-visible:ring-[var(--teal)]" />
          </div>
          <Button type="submit" disabled={busy} data-testid="register-submit" className="h-11 w-full bg-[var(--teal)] font-600 text-[#063b34] hover:bg-[#1ee9cf]">
            {busy ? "Creating…" : "Create account"}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-[#5b6878]">
          Already have an account?{" "}
          <Link to="/login" className="font-600 text-[var(--teal-deep)] hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
